import React, { useImperativeHandle, forwardRef, useCallback, useState, useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Extension } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Strike from '@tiptap/extension-strike';
import TranscriptLine from '../../extensions/TranscriptLine';
import LineNumbering, { documentHasLineNumbers, documentHasNumberableLines } from '../../extensions/LineNumbering';
import ClipboardSerializer, { splitHardBreaks } from '../../extensions/ClipboardSerializer';
import serializeToRichHTML from '../../utils/serializeToRichHTML';
import { buildTranscriptStyles } from '../../config/transcriptStyles';
import Toolbar from './Toolbar';
import InlineNotice from './InlineNotice';

// Re-create inline mark extensions without auto-formatting rules.
// This preserves the marks for HTML paste and programmatic use,
// but removes all markdown-style input rules (e.g., **bold**, *italic*)
// and paste rules that interfere with CA transcription notation.
const BoldNoRules = Bold.extend({
  addInputRules() { return []; },
  addPasteRules() { return []; },
});
const ItalicNoRules = Italic.extend({
  addInputRules() { return []; },
  addPasteRules() { return []; },
});
const StrikeNoRules = Strike.extend({
  addInputRules() { return []; },
  addPasteRules() { return []; },
});
// Cmd/Ctrl+U always stays inside the editor. The stock shortcut returns
// false when underline can't be applied, which lets the browser's own
// Ctrl+U (View Source) fire instead.
const UnderlineAlwaysHandled = Underline.extend({
  addKeyboardShortcuts() {
    return {
      'Mod-u': () => {
        this.editor.commands.toggleUnderline();
        return true;
      },
      'Mod-U': () => {
        this.editor.commands.toggleUnderline();
        return true;
      },
    };
  },
});

// Shift+Enter and Cmd/Ctrl+Enter start a new line just like Enter. The
// stock hard-break shortcuts would join lines inside one paragraph, and
// Ript numbers and classifies whole paragraphs. Runs ahead of HardBreak's
// own shortcuts; the hardBreak node itself stays so older files still load.
const ModifiedEnterAsEnter = Extension.create({
  name: 'modifiedEnterAsEnter',
  priority: 1000,
  addKeyboardShortcuts() {
    return {
      'Shift-Enter': () => this.editor.commands.enter(),
      'Mod-Enter': () => this.editor.commands.enter(),
    };
  },
});

// Drop marks the schema doesn't know (e.g. the inline `code` mark, which
// older documents may carry from pasted text) so loading such a file
// doesn't fail and fall back to an empty document.
function stripUnknownMarks(json, schema) {
  if (!json || typeof json !== 'object') return json;
  const out = { ...json };
  if (Array.isArray(out.marks)) {
    out.marks = out.marks.filter((mark) => schema.marks[mark.type]);
    if (out.marks.length === 0) delete out.marks;
  }
  if (Array.isArray(out.content)) {
    out.content = out.content.map((child) => stripUnknownMarks(child, schema));
  }
  return out;
}

const Editor = forwardRef(({ onContentChange, onStatusChange, spellcheckEnabled, fileActions, optionsActions, recoveryBanner }, ref) => {
  const [lineNumbersEnabled, setLineNumbersEnabled] = useState(false);
  const [charPx, setCharPx] = useState(null);
  const [notice, setNotice] = useState(null);
  const noticeTimerRef = useRef(null);

  // Show a transient, auto-dismissing notice beneath the toolbar
  const showNotice = useCallback((message) => {
    setNotice(message);
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    noticeTimerRef.current = setTimeout(() => setNotice(null), 6000);
  }, []);

  useEffect(() => () => {
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
  }, []);

  // Compute zoom font size (visual only — exports stay at 10pt)
  const zoomFontSize = `${10 * (optionsActions.zoomLevel / 100)}pt`;

  // Measure monospace character width (recalculates when zoom changes)
  useEffect(() => {
    const span = document.createElement('span');
    span.style.fontFamily = "'Courier New', Courier, monospace";
    span.style.fontSize = zoomFontSize;
    span.style.lineHeight = '1.5';
    span.style.position = 'absolute';
    span.style.visibility = 'hidden';
    span.style.whiteSpace = 'pre';
    span.textContent = 'X';
    document.body.appendChild(span);
    const width = span.getBoundingClientRect().width;
    document.body.removeChild(span);
    setCharPx(width);
  }, [zoomFontSize]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        paragraph: false,
        // Disable all extensions whose auto-formatting rules conflict with
        // CA transcription notation (*, -, +, >, ~, #, 1., etc.)
        // Block-level extensions — removed entirely (not used in CA)
        codeBlock: false,
        blockquote: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
        heading: false,
        horizontalRule: false,
        // Inline mark extensions — replaced below with rule-free versions
        bold: false,
        italic: false,
        strike: false,
        // Inline code is not offered either: it is invisible in a monospace
        // editor and excludes every other mark, so text pasted with it
        // (e.g. a transcript from a code block) could not be underlined.
        code: false,
      }),
      BoldNoRules,
      ItalicNoRules,
      StrikeNoRules,
      TranscriptLine,
      LineNumbering,
      UnderlineAlwaysHandled,
      ModifiedEnterAsEnter,
      ClipboardSerializer,
    ],
    // Global safety net — tells TipTap core not to collect any remaining rules
    enableInputRules: false,
    enablePasteRules: false,
    editorProps: {
      attributes: {
        spellcheck: spellcheckEnabled ? 'true' : 'false',
      },
    },
    content: '',
    onUpdate: ({ editor }) => {
      onContentChange();
      updateStatus(editor);
    },
    onSelectionUpdate: ({ editor }) => {
      updateStatus(editor);
    },
  });

  // Sync margin width setting into LineNumbering extension storage
  useEffect(() => {
    if (!editor) return;
    const newWidth = optionsActions.marginWidth;
    if (newWidth == null) return;
    const prev = editor.storage.lineNumbers.marginWidth;
    editor.storage.lineNumbers.marginWidth = newWidth;
    // If line numbers are currently applied and the width actually changed,
    // re-apply them so the user sees the new width immediately.
    if (prev !== newWidth && lineNumbersEnabled) {
      editor.commands.removeLineNumbers();
      editor.commands.addLineNumbers();
    }
  }, [editor, optionsActions.marginWidth, lineNumbersEnabled]);

  // Update spellcheck attribute when toggle changes
  useEffect(() => {
    if (!editor) return;
    editor.setOptions({
      editorProps: {
        attributes: {
          spellcheck: spellcheckEnabled ? 'true' : 'false',
        },
      },
    });
  }, [editor, spellcheckEnabled]);

  // Update guide line visibility and position
  useEffect(() => {
    if (!editor) return;
    const el = editor.view.dom;
    if (optionsActions.guideEnabled && charPx != null) {
      el.style.setProperty('--guide-left', `${10 + charPx * optionsActions.guideWidth}px`);
      el.classList.add('has-guide');
    } else {
      el.classList.remove('has-guide');
      el.style.removeProperty('--guide-left');
    }
  }, [editor, optionsActions.guideEnabled, optionsActions.guideWidth, charPx]);

  // Apply zoom font size to editor content (visual only)
  useEffect(() => {
    if (!editor) return;
    editor.view.dom.style.fontSize = zoomFontSize;
  }, [editor, zoomFontSize]);

  // Apply transcription mode: toggle the CSS class that suppresses
  // multimodal styling, and mirror the mode into extension storage so
  // keyboard shortcuts can read it. Switching modes never mutates the
  // underlying lineType/marginContent data — it only changes presentation.
  useEffect(() => {
    if (!editor) return;
    const mode = optionsActions.mode === 'audible' ? 'audible' : 'multimodal';
    editor.storage.lineNumbers.mode = mode;
    editor.view.dom.classList.toggle('mode-audible', mode === 'audible');
  }, [editor, optionsActions.mode]);

  // Apply multilingual setting: mirror it into extension storage (so the
  // gloss/translation shortcuts can read it) and toggle the CSS class that
  // styles gloss/translation lines. Like mode, this never mutates the
  // underlying lineType data — gloss/translation lines are preserved when off.
  useEffect(() => {
    if (!editor) return;
    const multilingual = !!optionsActions.multilingual;
    editor.storage.lineNumbers.multilingual = multilingual;
    editor.view.dom.classList.toggle('multilingual-on', multilingual);
  }, [editor, optionsActions.multilingual]);

  // Mirror the active line styles into the clipboard serializer's storage so
  // copy/cut and getRichHTML produce output matching the on-screen appearance.
  useEffect(() => {
    if (!editor) return;
    editor.storage.clipboardSerializer.config = buildTranscriptStyles(
      optionsActions.lineStyles,
      { mode: optionsActions.mode, multilingual: optionsActions.multilingual }
    );
  }, [editor, optionsActions.lineStyles, optionsActions.mode, optionsActions.multilingual]);

  const updateStatus = useCallback((ed) => {
    if (!ed) return;
    const { $from } = ed.state.selection;

    // Get line type of current paragraph
    const currentLineType = $from.parent.attrs?.lineType || 'default';

    // Calculate line and column
    const text = ed.getText();
    const { from } = ed.state.selection;
    const textBefore = text.slice(0, Math.min(from, text.length));
    const lines = textBefore.split('\n');
    const line = lines.length;
    const column = lines[lines.length - 1].length + 1;

    onStatusChange({
      line,
      column,
      charCount: text.length,
      lineType: currentLineType,
    });
  }, [onStatusChange]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    getJSON: () => editor?.getJSON() ?? null,
    getText: () => editor?.getText() ?? '',
    getHTML: () => editor?.getHTML() ?? '',
    getRichHTML: () => {
      if (!editor) return '';
      return serializeToRichHTML(
        editor.state.doc,
        editor.storage.clipboardSerializer?.config ?? undefined
      );
    },
    getDocument: () => editor?.state.doc ?? null,
    setContent: (content) => {
      if (!editor) return;
      editor.commands.setContent(stripUnknownMarks(content, editor.schema));
      // Files saved before paste splitting existed (or pasted into an older
      // version) can hold several transcript lines in one paragraph joined
      // by hard breaks; split them so each line can be numbered.
      const { doc } = editor.state;
      const lines = splitHardBreaks(doc.content);
      if (lines.childCount !== doc.childCount) {
        editor.commands.setContent(lines);
      }
      // Detect if loaded content has inline line numbers
      const hasNumbers = documentHasLineNumbers(editor.state.doc);
      setLineNumbersEnabled(hasNumbers);
    },
    clearContent: () => {
      if (!editor) return;
      editor.commands.clearContent();
    },
  }), [editor]);

  // Insert symbol at cursor position
  const insertSymbol = useCallback((symbol) => {
    if (!editor) return;
    editor.commands.insertContent(symbol);
    editor.commands.focus();
  }, [editor]);

  // Mark current line as speech (toggle)
  const markSpeech = useCallback(() => {
    if (!editor) return;
    editor.commands.toggleSpeechLine();
    editor.commands.focus();
  }, [editor]);

  // Mark current line as embodied (toggle)
  const markEmbodied = useCallback(() => {
    if (!editor) return;
    editor.commands.toggleEmbodiedLine();
    editor.commands.focus();
  }, [editor]);

  // Mark current line as gloss (toggle)
  const markGloss = useCallback(() => {
    if (!editor) return;
    editor.commands.toggleGlossLine();
    editor.commands.focus();
  }, [editor]);

  // Mark current line as translation (toggle)
  const markTranslation = useCallback(() => {
    if (!editor) return;
    editor.commands.toggleTranslationLine();
    editor.commands.focus();
  }, [editor]);

  // Mark current line as default
  const markDefault = useCallback(() => {
    if (!editor) return;
    editor.commands.setLineType('default');
    editor.commands.focus();
  }, [editor]);

  // Toggle line numbers (inline text)
  const toggleNumberLines = useCallback(() => {
    if (!editor) return;
    const isCurrentlyNumbered = documentHasLineNumbers(editor.state.doc);
    if (isCurrentlyNumbered) {
      editor.commands.removeLineNumbers();
      setLineNumbersEnabled(false);
    } else {
      // Nothing to number — guide the user instead of silently doing nothing
      if (!documentHasNumberableLines(editor.state.doc)) {
        const isAudible = optionsActions.mode === 'audible';
        showNotice(
          isAudible
            ? 'Nothing to number yet — mark your transcript lines first, then number.'
            : 'Nothing to number yet — mark speech lines first, then number.'
        );
        editor.commands.focus();
        return;
      }
      editor.commands.addLineNumbers();
      setLineNumbersEnabled(true);
    }
    editor.commands.focus();
  }, [editor, optionsActions.mode, showNotice]);

  // Reset all formatting
  const resetFormatting = useCallback(() => {
    if (!editor) return;
    // Remove inline line numbers first if present
    if (documentHasLineNumbers(editor.state.doc)) {
      editor.commands.removeLineNumbers();
    }
    editor.commands.setAllLineTypes('default');
    setLineNumbersEnabled(false);
    editor.commands.focus();
  }, [editor]);

  // Clear all inline formatting (except underline) and line classifications
  const clearFormatting = useCallback(() => {
    if (!editor) return;

    // Remove bold, italic, strike, and any other non-underline marks from entire document
    const { tr } = editor.state;
    const { doc } = tr;

    doc.descendants((node, pos) => {
      if (node.isText && node.marks.length > 0) {
        const marksToRemove = node.marks.filter(
          (mark) => mark.type.name !== 'underline'
        );
        marksToRemove.forEach((mark) => {
          tr.removeMark(pos, pos + node.nodeSize, mark.type);
        });
      }
    });

    editor.view.dispatch(tr);

    // Clear line classifications (but leave line numbers intact)
    editor.commands.setAllLineTypes('default');
    editor.commands.focus();
  }, [editor]);

  // Get current line type for toolbar active state
  const getCurrentLineType = useCallback(() => {
    if (!editor) return 'default';
    const { $from } = editor.state.selection;
    return $from.parent.attrs?.lineType || 'default';
  }, [editor]);

  return (
    <div className="editor-wrapper">
      <Toolbar
        editor={editor}
        onMarkSpeech={markSpeech}
        onMarkEmbodied={markEmbodied}
        onMarkGloss={markGloss}
        onMarkTranslation={markTranslation}
        onMarkDefault={markDefault}
        onNumberLines={toggleNumberLines}
        onResetFormatting={resetFormatting}
        onClearFormatting={clearFormatting}
        onInsertSymbol={insertSymbol}
        currentLineType={getCurrentLineType()}
        lineNumbersEnabled={lineNumbersEnabled}
        mode={optionsActions.mode === 'audible' ? 'audible' : 'multimodal'}
        multilingual={!!optionsActions.multilingual}
        fileActions={fileActions}
        optionsActions={optionsActions}
      />
      {recoveryBanner}
      <InlineNotice message={notice} onDismiss={() => setNotice(null)} />
      <EditorContent editor={editor} className="editor-content" />
    </div>
  );
});

Editor.displayName = 'Editor';

export default Editor;
